import React from 'react';
import moment from 'moment';
import { FiCheckCircle, FiMoreHorizontal } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useQuestionBook } from '@/hooks/useQuestionBook';
import { Dropdown } from '@/components/custom/dropdown/dropdown';
import { confirm } from '@/lib/confirm';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/hooks/useSession';
import IconExercises from '@/components/custom/icons/IconExercises';
import { Card, CardBody, CardBodyContent, CardBodyHeader, CardBodyHeaderContent, CardContent, CardFooter, CardIcon, CardIconImg, CardKpi, CardKpiGrid, CardKpiTitle, CardTitle } from '@/components/ui/card';
import { Badge, BadgeGroup, BadgeTruncate } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { isScheduledPublication } from '@/components/custom/schedule-publication-modal';

export function ExercisesCard(props) {
  // returnTo: rota para onde o "Salvar e sair" do exercício deve voltar (ex.: módulo de origem).
  const { book, returnTo } = props;
  const { t } = useTranslation();
  const { session } = useSession();

  const isPrivate = book?.creator?.id === session?.user?.id;

  if (!book) {
    return null;
  }

  const { deleteQuestionBook } = useQuestionBook();

  const confirmDestroy = async book => {
    const confirmed = await confirm({
      title: t('projects.warningDeleteQuestionBook'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      deleteQuestionBook(book.id);
    }
  };

  function difficulty(param) {
    switch (param) {
      case t('questionBooks.hard'):
        return 'danger';
      case t('questionBooks.medium'):
        return 'warning';
      default:
        return 'success';
    }
  }

  const isScheduled = isScheduledPublication(book);
  const releaseLabel = moment(book['published-at']).format('DD/MM/YYYY [às] HH:mm');
  const questionCount = book && book['question-count'] > 1 ? `${book['question-count']} ${t('exams.questions')}` : '1 questão';

  return (
    <Card
      hover
      isDanger={book?.['learning-system-question-books']?.[0]?.is_required}
    >
      <CardContent>
        <CardIcon>
          <CardIconImg>
            <IconExercises />
          </CardIconImg>
        </CardIcon>

        <CardBody>
          <CardBodyHeader>
            <CardBodyHeaderContent>
              <CardTitle>{book.title}</CardTitle>

              {isPrivate && (
                <Dropdown>
                  <Dropdown.Toggle>
                    <FiMoreHorizontal />
                  </Dropdown.Toggle>
                  <Dropdown.Content>
                    <Dropdown.Item onClick={() => confirmDestroy(book)}>{t('textsCommon.delete')}</Dropdown.Item>
                  </Dropdown.Content>
                </Dropdown>
              )}
            </CardBodyHeaderContent>

            {/* Badge obrigatório */}
            <BadgeGroup>
              {isScheduled && (
                <Badge
                  variant="warning"
                  size="xs"
                >
                  Em breve
                </Badge>
              )}
              {book?.['learning-system-question-books']?.[0]?.is_required === true && (
                <Badge
                  variant="danger"
                  size="xs"
                >
                  Obrigatório
                </Badge>
              )}
              <Badge
                variant={difficulty(book['t-difficulty'])}
                size="xs"
              >
                {book['t-difficulty']}
              </Badge>
            </BadgeGroup>
          </CardBodyHeader>

          <CardBodyContent>
            <BadgeTruncate
              variant="primary-light"
              size="sm"
              items={book.taxonomies}
              initialCount={2}
            />

            <CardKpiGrid>
              <CardKpi title={questionCount}>
                <FiCheckCircle /> {book['question-count']}
              </CardKpi>
              <CardKpi>
                <CardKpiTitle>{isScheduled ? 'Disponível em' : t('questionBooks.publishIn')}</CardKpiTitle>
                {isScheduled ? releaseLabel : moment(book['published-at']).format(t('datesForm.formatDate'))}
              </CardKpi>
            </CardKpiGrid>
          </CardBodyContent>
        </CardBody>
      </CardContent>

      <CardFooter>
        {isScheduled ? (
          <Button
            variant="outline"
            size="sm"
            fullWidth
            disabled
            title={`Disponível em ${releaseLabel}`}
          >
            Disponível em breve
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            fullWidth
            asChild
          >
            <Link
              to={`/questions/apostilas/${book.id}`}
              state={returnTo ? { returnTo } : undefined}
            >
              {t('questionBooks.buttonAccessQuestionBook')}
            </Link>
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
